import composeRefs from '@seznam/compose-react-refs';
import type { Ref } from 'react';
import { forwardRef, useRef, useState } from 'react';

import { Icon } from '@/components/Icon';
import type { iconSet } from '@/components/Icon/icons';

import type { InputComponentProps, InputState } from './DeprecatedInput.types';

import * as S from './DeprecatedInput.styles';

export const DeprecatedInput = forwardRef(
  (props: InputComponentProps, ref: Ref<HTMLInputElement>) => {
    const input = useRef<HTMLInputElement>(null);

    const handleWrapperClick = (e: React.SyntheticEvent) => {
      e.preventDefault();

      if (input.current) {
        input.current.focus();
      }
    };

    const {
      bg,
      colorVariant = 'default',
      disabled,
      errorMessage,
      fullWidth,
      isSelect,
      leftContent = null,
      onBlur,
      onChange,
      onFocus,
      onKeyUp,
      placeholder,
      readOnly,
      rightContent = null,
      size = 'm',
      state = 'initial',
      type,
      value = '',
      variant = 'underlined',
      ...rest
    } = props;

    const [inputType, setInputType] = useState(type);

    const handlePasswordVisibilityToggle = () => {
      if (inputType === 'password') {
        setInputType('text');
      }

      if (inputType === 'text') {
        setInputType('password');
      }
    };

    const renderPasswordIcon = () => {
      const isPasswordHidden = inputType === 'password';
      const isPasswordTypeFromProps = type === 'password';

      if (!isPasswordTypeFromProps) {
        return null;
      }

      return (
        <Icon
          data-test-id='DeprecatedInputIcon'
          name={isPasswordHidden ? 'eye' : 'eyeClosed'}
          onClick={handlePasswordVisibilityToggle}
          size='l'
          variant='regular'
        />
      );
    };

    const iconStateMap: { [K in InputState]?: { name: keyof typeof iconSet } } =
      {
        error: { name: 'close' },
        warning: { name: 'infoBadge' },
        success: { name: 'check' },
      };

    const iconStateProps = iconStateMap[state];

    const stateIcon = iconStateProps && (
      <Icon
        size='m'
        variant='regular'
        {...iconStateProps}
        data-test-id='DeprecatedInputStateIcon'
      />
    );

    const rightIcon = stateIcon || renderPasswordIcon() || rightContent;
    const hasPlaceholder = !!placeholder;

    return (
      <>
        <S.Wrapper
          bg={bg}
          colorVariant={colorVariant}
          data-test-id='DeprecatedInputWrapper'
          disabled={disabled}
          fullWidth={fullWidth}
          onClick={handleWrapperClick}
          size={size}
          state={state}
          variant={variant}
        >
          {leftContent && (
            <S.LeftContentWrapper data-test-id='DeprecatedInputLeftContentWrapper'>
              {leftContent}
            </S.LeftContentWrapper>
          )}
          <S.Input
            ref={composeRefs(ref, input)}
            disabled={disabled}
            hasPlaceholder={hasPlaceholder}
            inputSize={size}
            onBlur={onBlur}
            onChange={(e) => {
              if (!disabled && onChange) onChange(e);
            }}
            onFocus={onFocus}
            onKeyUp={onKeyUp}
            placeholder={isSelect ? placeholder : ''}
            readOnly={readOnly}
            type={inputType}
            value={value}
            {...rest}
            data-test-id='DeprecatedInputField'
          />
          {!isSelect && (
            <S.Placeholder
              colorVariant={colorVariant}
              data-test-id='DeprecatedInputPlaceholder'
              isLeftContentVisible={!!leftContent}
              size={size}
            >
              {placeholder}
            </S.Placeholder>
          )}
          {rightIcon}
        </S.Wrapper>
        {errorMessage && (
          <S.ErrorMessage data-test-id='DeprecatedInputErrorMessage'>
            {errorMessage}
          </S.ErrorMessage>
        )}
      </>
    );
  },
);

DeprecatedInput.defaultProps = {
  colorVariant: 'default',
  variant: 'underlined',
  value: '',
};

DeprecatedInput.displayName = 'Input';
